import { useEffect, useRef, useState } from "react";
import { NodeViewProps, NodeViewWrapper } from "@tiptap/react";
import api from "@/lib/api-client";
import {
  ActionIcon,
  Modal,
  Stack,
  Tooltip,
  Group,
  Button,
  Text,
  TextInput,
  Textarea,
} from "@mantine/core";
import {
  IconExternalLink,
  IconPencil,
  IconWorld,
  IconRefresh,
  IconCopy,
  IconCheck,
  IconTrash,
  IconAlertTriangle,
  IconFile,
} from "@tabler/icons-react";
import classes from "./embed-url.module.css";
import { LinkCascadePicker } from "@/features/editor/components/link/link-cascade-picker.tsx";
import type { IPage } from "@/features/page/types/page.types.ts";

// Internal URLs are doc-tree links picked from the LinkCascadePicker —
// they start with "/" (e.g. "/docs/foo/bar" or "/foo/bar"). External
// embeds are absolute http(s) URLs.
function isInternalPath(s: string): boolean {
  return /^\/[^/]/.test(s) || s === "/";
}

function safeHost(url: string): string | null {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

function faviconFromHost(host: string | null): string | null {
  if (!host) return null;
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=64`;
}

export default function EmbedUrlView(props: NodeViewProps) {
  const { node, editor, updateAttributes, selected } = props;
  const canEdit = editor.isEditable;
  const url = (node.attrs.url as string) || "";
  const internal = isInternalPath(url);
  const host = internal
    ? (node.attrs.provider as string | null) || "Page"
    : (node.attrs.provider as string | null) ?? safeHost(url);
  const customTitle = (node.attrs.customTitle as string | null) || null;
  const customDescription =
    (node.attrs.customDescription as string | null) || null;
  // Display title precedence: user-supplied custom > fetched/page title >
  // host > raw URL. customTitle survives a metadata refresh.
  const title = customTitle || (node.attrs.title as string | null) || host || url;
  const description =
    customDescription ?? (node.attrs.description as string | null) ?? host ?? "";
  const favicon = internal
    ? null
    : (node.attrs.favicon as string | null) ?? faviconFromHost(host);

  const [editOpen, setEditOpen] = useState(!url && canEdit);
  const [draftUrl, setDraftUrl] = useState(url);
  const [draftCustomTitle, setDraftCustomTitle] = useState(customTitle ?? "");
  const [draftCustomDescription, setDraftCustomDescription] = useState(
    customDescription ?? "",
  );
  const [draftErr, setDraftErr] = useState<string | null>(null);

  // If node is inserted without a URL (from slash menu), open the modal
  // automatically so the user can paste/type the URL right away.
  useEffect(() => {
    if (!url && canEdit) setEditOpen(true);
  }, [url, canEdit]);

  // Remove the empty-shell embed if user cancels the first URL entry.
  const onCancelEdit = () => {
    setEditOpen(false);
    if (!url && typeof props.deleteNode === 'function') {
      props.deleteNode();
    }
  };
  const [fetchState, setFetchState] = useState<
    'idle' | 'loading' | 'ready' | 'error'
  >('idle');
  const [copied, setCopied] = useState(false);

  // Auto-fetch metadata on first render if title is missing and URL is set.
  const fetchedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!canEdit) return;
    if (!url) return;
    // Internal doc-tree links carry their metadata from the picker — never
    // hit /embed/metadata for relative URLs (it would fail anyway).
    if (isInternalPath(url)) {
      setFetchState('ready');
      return;
    }
    if (node.attrs.title) {
      setFetchState('ready');
      return;
    }
    if (fetchedForRef.current === url) return;
    fetchedForRef.current = url;
    setFetchState('loading');
    const ctrl = new AbortController();
    api
      .get(`/embed/metadata?url=${encodeURIComponent(url)}`, {
        signal: ctrl.signal as any,
      })
      .then((res: any) => {
        const d = res as {
          title?: string;
          description?: string;
          favicon?: string;
          provider?: string;
          partial?: boolean;
        };
        updateAttributes({
          title: d?.title ?? null,
          description: d?.description ?? null,
          favicon: d?.favicon ?? null,
          provider: d?.provider ?? safeHost(url),
        });
        setFetchState(d?.partial ? 'error' : 'ready');
      })
      .catch(() => setFetchState('error'));
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, canEdit]);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      /* clipboard denied — no-op */
    }
  };

  const onRemove = () => {
    if (typeof props.deleteNode === 'function') props.deleteNode();
  };

  // Page metadata staged when the picker selects a doc-tree page; consumed
  // on commit so the embed shows the page title without a metadata fetch.
  const pickedPageRef = useRef<{
    page: Partial<IPage>;
    url: string;
  } | null>(null);

  const commitEdit = (overrideUrl?: string) => {
    let next = (overrideUrl ?? draftUrl).trim();
    if (!next) {
      setDraftErr("URL is required");
      return;
    }
    // Public docs now live at the subdomain root; the picker still emits
    // legacy /docs/<urlPath>. Strip the prefix so the stored embed URL is
    // canonical and doesn't ride a 308 redirect on every click.
    if (next.startsWith('/docs/')) next = next.slice('/docs'.length);
    if (next === '/docs') next = '/';
    const nextCustom = draftCustomTitle.trim() || null;
    const nextCustomDesc = draftCustomDescription.trim() || null;
    if (isInternalPath(next)) {
      // Internal doc-tree URL — pull title from the picker selection if
      // available, otherwise fall back to the path as the title.
      const picked =
        pickedPageRef.current && pickedPageRef.current.url === next
          ? pickedPageRef.current.page
          : null;
      updateAttributes({
        url: next,
        provider: 'Page',
        title: picked?.title ?? next,
        description: null,
        favicon: null,
        customTitle: nextCustom,
        customDescription: nextCustomDesc,
      });
      pickedPageRef.current = null;
      setEditOpen(false);
      return;
    }
    try {
      // eslint-disable-next-line no-new
      new URL(next);
    } catch {
      setDraftErr("Please enter a valid URL or pick a page from the list");
      return;
    }
    // URL changed → drop the cached metadata so it gets re-fetched. Keep
    // the user's customTitle (it's URL-agnostic by design).
    const urlChanged = next !== url;
    updateAttributes({
      url: next,
      provider: safeHost(next),
      title: urlChanged ? null : (node.attrs.title as string | null),
      description: urlChanged ? null : (node.attrs.description as string | null),
      favicon: urlChanged ? null : (node.attrs.favicon as string | null),
      customTitle: nextCustom,
      customDescription: nextCustomDesc,
    });
    pickedPageRef.current = null;
    setEditOpen(false);
  };

  const onCardClick = (e: React.MouseEvent) => {
    if (!canEdit) return;
    if (e.metaKey || e.ctrlKey) return;
    e.preventDefault();
  };

  // Empty placeholder while user is typing URL in the modal.
  if (!url) {
    return (
      <NodeViewWrapper
        className={classes.wrapper}
        data-type="embed-url"
        data-selected={selected ? "true" : undefined}
      >
        <div
          className={classes.card}
          role="button"
          onClick={() => canEdit && setEditOpen(true)}
          style={{ cursor: canEdit ? "pointer" : "default" }}
        >
          <span className={classes.favicon}>
            <IconWorld size={20} />
          </span>
          <span className={classes.content}>
            <span className={classes.title}>Embed a URL</span>
            <span className={classes.description}>
              Click to paste or type the URL
            </span>
          </span>
        </div>
        <Modal
          opened={editOpen}
          onClose={onCancelEdit}
          title="Embed a URL"
          centered
          size="md"
        >
          <Stack gap="xs">
            <Text size="xs" c="dimmed">
              Paste any URL or search your docs to link to a page or
              heading. Internal links open in the same tab.
            </Text>
            <LinkCascadePicker
              autoFocus
              inputSize="sm"
              placeholder="https://example.com/page  or search pages…"
              value={draftUrl}
              // No `onCommit` — picking a page or pressing Enter inside the
              // picker only stages the URL. The user still needs to confirm
              // via Insert (and may add a Display name first).
              onChange={(href) => {
                setDraftUrl(href);
                setDraftErr(null);
              }}
              onPickPage={(page, href) => {
                pickedPageRef.current = { page, url: href };
                if (!draftCustomTitle && page.title) {
                  setDraftCustomTitle("");
                }
              }}
            />
            <TextInput
              label="Display name"
              description="Optional — shown instead of the URL or fetched title."
              placeholder="e.g. Auto qq"
              size="sm"
              value={draftCustomTitle}
              onChange={(e) => setDraftCustomTitle(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitEdit();
                }
              }}
            />
            <Textarea
              label="Description"
              description="Optional — shown under the title on the embed button."
              placeholder="e.g. Issue per-order credentials so apps can connect through these IPs."
              size="sm"
              autosize
              minRows={2}
              maxRows={4}
              value={draftCustomDescription}
              onChange={(e) =>
                setDraftCustomDescription(e.currentTarget.value)
              }
            />
            {draftErr ? (
              <Text size="xs" c="red">
                {draftErr}
              </Text>
            ) : null}
            <Group justify="flex-end">
              <Button variant="default" onClick={onCancelEdit}>
                Cancel
              </Button>
              <Button
                onClick={() => commitEdit()}
                disabled={!draftUrl.trim()}
              >
                Insert
              </Button>
            </Group>
          </Stack>
        </Modal>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper
      className={classes.wrapper}
      data-type="embed-url"
      data-selected={selected ? "true" : undefined}
    >
      <a
        href={url || "#"}
        target={internal ? undefined : "_blank"}
        rel={internal ? undefined : "noopener noreferrer"}
        className={classes.card}
        onClick={onCardClick}
        aria-label={`${title}${host ? ` — ${host}` : ""}`}
        data-state={fetchState}
      >
        <span className={classes.favicon}>
          {internal ? (
            <IconFile size={20} />
          ) : fetchState === 'loading' ? (
            <span className={classes.skeletonFavicon} />
          ) : fetchState === 'error' ? (
            <IconAlertTriangle size={20} />
          ) : favicon ? (
            <img
              src={favicon}
              alt=""
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
          ) : (
            <IconWorld size={20} />
          )}
        </span>
        <span className={classes.content}>
          {fetchState === 'loading' ? (
            <>
              <span className={`${classes.skeletonLine} ${classes.skeletonTitle}`} />
              <span className={`${classes.skeletonLine} ${classes.skeletonDesc}`} />
            </>
          ) : (
            <>
              <span className={classes.title}>{title}</span>
              <span className={classes.description}>
                {fetchState === 'error'
                  ? 'Unable to load preview'
                  : description}
              </span>
            </>
          )}
        </span>
        <span className={classes.cta}>
          <IconExternalLink size={14} />
          <span>Open</span>
        </span>
      </a>

      {canEdit && (
        <div className={classes.actions}>
          <Tooltip label="Edit URL" withArrow>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              onClick={() => {
                setDraftUrl(url);
                setDraftCustomTitle(customTitle ?? "");
                setDraftCustomDescription(customDescription ?? "");
                setDraftErr(null);
                setEditOpen(true);
              }}
              aria-label="Edit URL"
            >
              <IconPencil size={14} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Refresh metadata" withArrow>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              onClick={() => {
                fetchedForRef.current = null;
                setFetchState('idle');
                updateAttributes({
                  title: null,
                  description: null,
                  favicon: null,
                });
              }}
              aria-label="Refresh metadata"
            >
              <IconRefresh size={14} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={copied ? 'Copied' : 'Copy URL'} withArrow>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              onClick={onCopy}
              aria-label="Copy URL"
            >
              {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Remove" withArrow>
            <ActionIcon
              variant="subtle"
              color="red"
              size="sm"
              onClick={onRemove}
              aria-label="Remove embed"
            >
              <IconTrash size={14} />
            </ActionIcon>
          </Tooltip>
        </div>
      )}

      <Modal
        opened={editOpen}
        onClose={onCancelEdit}
        title="Embed a URL"
        centered
        size="md"
      >
        <Stack gap="xs">
          <Text size="xs" c="dimmed">
            Paste any URL or search your docs to link to a page or
            heading. Internal links open in the same tab.
          </Text>
          <LinkCascadePicker
            autoFocus
            inputSize="sm"
            placeholder="https://example.com/page  or search pages…"
            value={draftUrl}
            onChange={(href) => {
              setDraftUrl(href);
              setDraftErr(null);
            }}
            onCommit={(href) => {
              setDraftUrl(href);
              setDraftErr(null);
              commitEdit(href);
            }}
            onPickPage={(page, href) => {
              pickedPageRef.current = { page, url: href };
            }}
          />
          <TextInput
            label="Display name"
            description="Optional — shown instead of the URL or fetched title."
            placeholder="e.g. Authorization"
            size="sm"
            value={draftCustomTitle}
            onChange={(e) => setDraftCustomTitle(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitEdit();
              }
            }}
          />
          <Textarea
            label="Description"
            description="Optional — shown under the title on the embed button."
            placeholder="e.g. Issue per-order credentials so apps can connect through these IPs."
            size="sm"
            autosize
            minRows={2}
            maxRows={4}
            value={draftCustomDescription}
            onChange={(e) => setDraftCustomDescription(e.currentTarget.value)}
          />
          {draftErr ? (
            <Text size="xs" c="red">
              {draftErr}
            </Text>
          ) : null}
          <Group justify="flex-end">
            <Button variant="default" onClick={onCancelEdit}>
              Cancel
            </Button>
            <Button
              onClick={() => commitEdit()}
              disabled={!draftUrl.trim()}
            >
              Insert
            </Button>
          </Group>
        </Stack>
      </Modal>
    </NodeViewWrapper>
  );
}
